import { METADATA_TYPE } from "@/constants/metadata-types"
import { Button, SelectionChip } from "@coval/ui"
import { Copy, Pencil, Plus, Trash2 } from "lucide-react"

import type { ConversationMetricRule } from "@/types/api/ConversationMetricRule"

import {
  formatConversationMetricRuleMatchType,
  formatConversationMetricRuleMetadataValue,
} from "../utils/conversationMetricRulePresentation"
import {
  getRuleMetadataConditions,
  getRuleMetrics,
} from "../utils/conversationMetricsConfigurationRules"

interface MetricDisplay {
  id?: string
  metric_name?: string
  metric_display_name?: string
}

interface ConversationMetricsConfigurationOverviewProps {
  view?: "all" | "default" | "conditional"
  metricsByName?: Record<string, MetricDisplay>
  defaultMetricIds: string[]
  rules: ConversationMetricRule[]
  isConfigurationUnavailable: boolean
  metricsPerCallLimit: number | null
  onEditDefaultMetrics: () => void
  onRemoveDefaultMetric: (metricId: string) => void
  onAddRule: () => void
  onEditRule: (rule: ConversationMetricRule) => void
  onCopyRule: (rule: ConversationMetricRule) => void
  onDeleteRule: (ruleId: string) => void
}

export function ConversationMetricsConfigurationOverview({
  view = "all",
  metricsByName,
  defaultMetricIds,
  rules,
  isConfigurationUnavailable,
  metricsPerCallLimit,
  onEditDefaultMetrics,
  onRemoveDefaultMetric,
  onAddRule,
  onEditRule,
  onCopyRule,
  onDeleteRule,
}: ConversationMetricsConfigurationOverviewProps) {
  const catalogMetrics = Object.values(metricsByName ?? {})
  const findMetric = (identifier: string) =>
    catalogMetrics.find((metric) => metric.id === identifier) ??
    metricsByName?.[identifier] ??
    catalogMetrics.find((metric) => metric.metric_name === identifier)

  const defaultsExceedPerCallLimit =
    metricsPerCallLimit !== null &&
    defaultMetricIds.length > metricsPerCallLimit

  const defaultMetrics = defaultMetricIds
    .map((metricId) => ({
      metricId,
      label: findMetric(metricId)?.metric_display_name || metricId,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    )

  return (
    <>
      {view !== "conditional" && (
        <section className="rounded-lg border border-coval-border">
          <div className="flex items-center justify-between px-6 py-5">
            <div>
              <h2 className="text-xl font-semibold">Default Metrics</h2>
              <p className="mt-1 text-sm text-coval-text-muted">
                Metrics automatically added to all runs.
              </p>
            </div>
            <Button
              variant="default"
              onClick={onEditDefaultMetrics}
              aria-label="Add metric"
              disabled={isConfigurationUnavailable}
            >
              <Plus className="h-4 w-4" />
              Add Metric
            </Button>
          </div>
          <div className="border-t border-coval-border px-6 py-4">
            {defaultMetricIds.length === 0 ? (
              <div className="py-6 text-center text-coval-text-muted">
                No default metrics configured. Click &quot;Add Metric&quot; to
                add metrics.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {defaultMetrics.map(({ metricId, label }) => (
                  <SelectionChip
                    key={metricId}
                    label={label}
                    onRemove={() => onRemoveDefaultMetric(metricId)}
                  />
                ))}
              </div>
            )}
            {defaultsExceedPerCallLimit && (
              <p className="mt-3 text-sm text-amber-600">
                This evaluates {defaultMetricIds.length} metrics per call; your
                plan allows {metricsPerCallLimit}. Uploads will be rejected
                until you remove metrics or upgrade your plan.
              </p>
            )}
          </div>
        </section>
      )}

      {view !== "default" && (
        <section className="rounded-lg border border-coval-border">
          <div className="flex items-center justify-between px-6 py-5">
            <div>
              <h2 className="text-xl font-semibold">Conditional Metrics</h2>
              <p className="mt-1 text-sm text-coval-text-muted">
                Conditionally add metrics based on run metadata.
              </p>
            </div>
            <Button
              variant="default"
              onClick={onAddRule}
              aria-label="Add rule"
              disabled={isConfigurationUnavailable}
            >
              <Plus className="h-4 w-4" />
              Add Rule
            </Button>
          </div>
          <div className="border-t border-coval-border px-6 py-4">
            {rules.length === 0 ? (
              <div className="py-6 text-center text-coval-text-muted">
                No conditional metrics configured. Click &quot;Add Rule&quot; to
                create your first rule.
              </div>
            ) : (
              <div className="space-y-3">
                {rules.map((rule, ruleIndex) => {
                  const ruleMetrics = getRuleMetrics(rule)
                  const ruleMetadataConditions = getRuleMetadataConditions(rule)

                  return (
                    <div
                      key={rule.id}
                      className="rounded-md border border-coval-border p-4 text-sm"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1 space-y-4">
                          <p className="font-medium text-coval-text">
                            Rule {ruleIndex + 1}
                          </p>

                          <div className="space-y-2">
                            <p className="text-xs font-medium text-coval-text-muted">
                              When all conditions match
                            </p>
                            <ul className="space-y-2">
                              {ruleMetadataConditions.map(
                                (condition, conditionIndex) => (
                                  <li
                                    key={conditionIndex}
                                    className="flex flex-wrap items-center gap-2 before:text-coval-text-muted before:content-['•']"
                                  >
                                    <span className="rounded-md border border-coval-border bg-surface-1 px-2 py-1 font-mono text-xs text-coval-text">
                                      {condition.metadataKey}
                                    </span>
                                    <span className="text-coval-text-muted">
                                      {formatConversationMetricRuleMatchType(
                                        condition.matchType,
                                      )}
                                    </span>
                                    {condition.metadataType !==
                                    METADATA_TYPE.BOOLEAN ? (
                                      <span className="rounded-md border border-coval-border bg-surface-1 px-2 py-1 font-mono text-xs text-coval-text">
                                        {formatConversationMetricRuleMetadataValue(
                                          condition.metadataType,
                                          condition.metadataValue,
                                        )}
                                      </span>
                                    ) : null}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>

                          <div className="space-y-2">
                            <p className="text-xs font-medium text-coval-text-muted">
                              Add metrics
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {ruleMetrics.map((metricName) => (
                                <SelectionChip
                                  key={metricName}
                                  label={
                                    findMetric(metricName)
                                      ?.metric_display_name || metricName
                                  }
                                />
                              ))}
                            </div>
                          </div>

                          {metricsPerCallLimit !== null &&
                            defaultMetricIds.length + ruleMetrics.length >
                              metricsPerCallLimit && (
                              <p className="text-sm text-amber-600">
                                A matching upload evaluates{" "}
                                {defaultMetricIds.length + ruleMetrics.length}{" "}
                                metrics (default + rule); your plan allows{" "}
                                {metricsPerCallLimit}. These uploads will be
                                rejected.
                              </p>
                            )}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <Button
                            type="button"
                            icon
                            variant="ghost"
                            onClick={() => onEditRule(rule)}
                            tooltip="Edit rule"
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            icon
                            variant="ghost"
                            onClick={() => onCopyRule(rule)}
                            tooltip="Duplicate rule"
                          >
                            <Copy className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            icon
                            variant="ghost"
                            onClick={() => onDeleteRule(rule.id)}
                            tooltip="Delete rule"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      )}
    </>
  )
}
